import { useMemo } from "react";
import { ChevronLeft, LogOut, Menu as MenuIcon } from "lucide-react";

import { SidebarItemsType } from "@/types/sidebar";
import { getUserAvatarColor } from "@/design-system/tokens";
import SidebarNav from "./SidebarNav";
import Sheet from "@/components/ui/sheet";
import Tooltip from "@/components/ui/tooltip";
import DropdownMenu from "@/components/ui/dropdown-menu";
import aticaLogo from "@/layouts/assets/logo6.svg";
import useAuth from "@/hooks/useAuth";
import { useIsInternalUser } from "@/hooks/useIsInternalUser";
import { useEntitlements } from "@/hooks/useEntitlements";
import { SIDEBAR_COLLAPSED_WIDTH, centerInCollapsedRail } from "./constants";
import AticaAnimatedLogo from "./AticaAnimatedLogo";
import { cn } from "@/utils/cn";

// Matches the avatar's own h-9 w-9 class below — kept as a constant (same
// pattern as SidebarNavListItem.tsx's ROW_ICON_SIZE) so it can be passed to
// centerInCollapsedRail instead of a bare number at the call site.
const AVATAR_SIZE = 36;

export type SidebarProps = {
  items: {
    title: string;
    pages: SidebarItemsType[];
  }[];
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  variant?: "permanent" | "temporary";
  open?: boolean;
  onClose?: () => void;
  // No default — always supplied explicitly by PricingAudit.tsx's own
  // drawerWidth constant, so there's exactly one source for this number.
  width: number;
};

function Sidebar({
  items,
  collapsed = false,
  onToggleCollapse,
  variant = "permanent",
  open = false,
  onClose,
  width,
}: SidebarProps) {
  const { user, signOut } = useAuth();
  const isInternal = useIsInternalUser();
  const { loading: entitlementsLoading, capabilities } = useEntitlements();

  const userName =
    user?.name || user?.displayName || user?.preferred_username || "";

  const userInitials = useMemo(() => {
    if (!userName) return "?";
    return userName
      .split(" ")
      .map((n: string) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  }, [userName]);

  const avatarColor = useMemo(
    () => getUserAvatarColor(user?.email || userName || ""),
    [user?.email, userName]
  );

  // Rendered once and reused for both the trigger button and the dropdown's
  // identity header below, instead of two copies of the same markup drifting
  // out of sync with each other.
  const avatar = (
    <span
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[0.8125rem] font-semibold text-white"
      style={{ backgroundColor: avatarColor }}
    >
      {userInitials}
    </span>
  );

  const visibleItems = useMemo(() => {
    if (isInternal) return items;
    return items
      .map((section) => ({
        ...section,
        pages: section.pages.filter((p) => {
          if (!p.visibleToExternal) return false;
          if (!p.requires) return true;
          // Held back rather than shown-then-removed while contracts resolve:
          // a nav item that vanishes under the cursor is worse than one that
          // arrives a beat late.
          return entitlementsLoading ? false : capabilities.includes(p.requires);
        }),
      }))
      .filter((section) => section.pages.length > 0);
  }, [items, isInternal, entitlementsLoading, capabilities]);

  const effectiveWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : width;

  const content = (
    <>
      <div
        className={cn(
          "relative flex min-h-[56px] shrink-0 items-center justify-center gap-2 bg-sidebar-background sm:min-h-[64px]",
          collapsed ? "px-2" : "px-6"
        )}
      >
        {collapsed ? (
          <Tooltip content="Expand sidebar" side="right">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse?.();
              }}
              aria-label="Expand sidebar"
              className="flex h-8 w-8 items-center justify-center border-0 bg-transparent text-sidebar-color transition-opacity hover:opacity-80"
            >
              <MenuIcon size={24} />
            </button>
          </Tooltip>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            {isInternal ? (
              <AticaAnimatedLogo className="h-12" />
            ) : (
              <img
                src={aticaLogo}
                alt="Atica"
                className="h-12 w-auto rounded"
              />
            )}
          </div>
        )}
        {onToggleCollapse && !collapsed && (
          <Tooltip content="Collapse sidebar">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onToggleCollapse();
              }}
              aria-label="Collapse sidebar"
              className="absolute right-1 flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent text-sidebar-color transition-colors hover:bg-white/10"
            >
              <ChevronLeft size={20} />
            </button>
          </Tooltip>
        )}
      </div>

      <SidebarNav items={visibleItems} collapsed={collapsed} />

      <DropdownMenu
        side="top"
        align="start"
        // w-max + a cap (rather than a fixed width) lets the panel grow to
        // fit the email instead of truncating it — most logins here are
        // short corporate addresses, so this rarely hits the cap at all.
        contentClassName="w-max max-w-[320px]"
        // Identity block is display-only (avatar/name/email), separated from
        // the item list by a divider — see DropdownMenu's `header` prop.
        // Future entries (Settings, appearance, etc.) are just more objects
        // in `items` below, no layout changes needed.
        header={
          <div className="flex items-center gap-3">
            {avatar}
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-[0.8125rem] font-semibold text-text-heading">
                {userName}
              </span>
              {user?.email && (
                <span className="break-words text-xs text-text-muted">
                  {user.email}
                </span>
              )}
            </div>
          </div>
        }
        items={[
          {
            label: "Sign out",
            onSelect: signOut,
            icon: <LogOut size={15} aria-hidden="true" />,
            variant: "danger",
          },
        ]}
        trigger={
          <button
            type="button"
            // pl is computed (see rowStyle in SidebarNavListItem.tsx for the
            // identical technique) and kept fixed collapsed or not — that's
            // what centers the 36px avatar in the 64px rail without it
            // jumping sideways when the sidebar toggles.
            style={{ paddingLeft: centerInCollapsedRail(AVATAR_SIZE) }}
            className={cn(
              "mt-auto flex items-center justify-start gap-4 border-0 bg-sidebar-background py-3 transition-[padding] duration-300 hover:brightness-90 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white",
              collapsed ? "pr-3.5" : "pr-7"
            )}
            aria-label={
              userName ? `Account menu for ${userName}` : "Account menu"
            }
          >
            <Tooltip content={collapsed ? userName : ""} side="right">
              {avatar}
            </Tooltip>
            {!collapsed && (
              <span className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left text-sm font-semibold text-sidebar-color/90">
                {userName}
              </span>
            )}
          </button>
        }
      />
    </>
  );

  if (variant === "temporary") {
    return (
      <Sheet
        open={open}
        onOpenChange={(next) => {
          if (!next) onClose?.();
        }}
        title="Navigation"
        width={width}
        className="flex-col bg-sidebar-background"
      >
        {content}
      </Sheet>
    );
  }

  return (
    <aside
      style={{ width: effectiveWidth }}
      className="flex h-full shrink-0 flex-col overflow-y-auto overflow-x-hidden bg-sidebar-background transition-[width] duration-300 ease-in-out"
    >
      {content}
    </aside>
  );
}

export default Sidebar;
